import type { Strings } from '../index.ts';

/**
 * Die Anmeldeseite.
 *
 * **`explanation.theInstance` steht im Dativ**, und das ist keine Nachlässigkeit, sondern die einzige
 * Form, die überall passt. Der Wert ersetzt den Hostnamen an fünf Stellen (`model.ts` für die vier
 * Erklärungen, `view.tsx` für die Fußnote), und ein Hostname ist kasusneutral, ein Substantiv nicht. Alle
 * fünf Sätze sind deshalb so gebaut, dass die Lücke hinter einer Dativpräposition steht: „bei“, „auf“,
 * „zu“. Ein Satz mit „für“ hätte den Akkusativ verlangt und aus derselben Lücke „für deiner Instanz“
 * gemacht — deshalb sagt `unreadable` „Zu ${host} ist eine Sitzung gespeichert“ und nicht „Für“.
 *
 * Der Sitzungsblock ist eine **Label/Wert-Liste und kein Satz**, hier wie im Englischen: „Läuft ab · in
 * 27 Tagen“ kann kein Verb mit einer fremdsprachigen Zeitangabe mischen, weil es kein Verb hat.
 */
export const login: Strings['login'] = {
  headline: {
    checking: 'Dieser Browser wird auf eine Sitzung geprüft…',
    signedInAnonymous: 'Bei dieser Instanz angemeldet',
    signedInAs: (username) => `Angemeldet als ${username}`,
    expired: 'Diese Sitzung ist abgelaufen',
    unreadable: 'Die gespeicherte Sitzung lässt sich nicht lesen',
    signedOut: 'Bei deiner Instanz anmelden',
    storeFailed: 'Dieser Browser wollte die Sitzung nicht speichern',
    emptyToken: 'Die Instanz hat nichts zurückgeschickt',
    denied: 'Es wurde nichts freigegeben',
  },

  explanation: {
    theInstance: 'deiner Instanz',
    active: (host) =>
      `Du bist bei ${host} angemeldet. Die Sitzung liegt nur in diesem Browser und nur für diese Instanz, und beim Abmelden wird sie gelöscht.`,
    underScoped: (host) =>
      `Deine Sitzung auf ${host} funktioniert, deckt aber nicht alles ab, worum diese Version von Visious bittet. Alles unten, was als „nicht erteilt“ markiert ist, wird abgelehnt, bis du dich neu anmeldest.`,
    expired:
      'Sitzungen laufen nach einer Weile ab, und diese ist es. Eine neue Anmeldung genügt, und an deinem Konto ändert sich nichts.',
    unreadable: (host) =>
      `Zu ${host} ist eine Sitzung gespeichert, die Visious nicht lesen kann. Eine neue Anmeldung ersetzt sie.`,
    signedOut: (host) =>
      `Abos, deine Playlists und dein Verlauf liegen auf ${host} und nicht in Visious. Die Anmeldung schickt dich auf die Freigabeseite der Instanz selbst. Visious sieht dein Passwort nie, nur das, was die Instanz zurückgibt.`,
  },

  notice: {
    unsolicited:
      'Dieser Link brachte eine Anmeldung für ein Konto mit, und in diesem Tab hatte niemand um eine gebeten, deshalb wurde sie verworfen statt gespeichert. Ein solcher Link meldet dich im Konto einer anderen Person an, wo alles aufgezeichnet wird, was du dann ansiehst. Fang die Anmeldung stattdessen auf dieser Seite an.',
    instanceMismatch: (granted, current) =>
      `Diese Freigabe kam für ${granted} zurück, aber Visious spricht jetzt mit ${current}. Es wurde nichts gespeichert: eine Sitzung von einer Instanz gilt nie auf einer anderen.`,
    storeFailed:
      'Die Instanz hat dich angemeldet, und dieser Browser wollte es nicht behalten. Das bedeutet privates Surfen mit blockiertem Speicher, oder eine volle Festplatte. Eine neue Anmeldung tut dasselbe, ändern muss sich also die Speicherberechtigung für diese Seite.',
    verifyFailed: (title, detail) => `${title}. ${detail}`,
    emptyToken:
      'Die Instanz hat dich ohne Sitzung und ohne Ablehnung zurückgeschickt, es gab also nichts zu speichern. Ein zweiter Versuch lohnt sich. Wiederholt es sich, meldet die Instanz niemanden an.',
    denied:
      'Eine Anmeldung wurde begonnen und es kam nichts zurück. Entweder wurde sie auf der Instanz abgelehnt, oder die Freigabeseite wurde ohne Freigabe verlassen. Es wurde nichts erteilt und nichts gespeichert.',
    underScoped: (missing, total) =>
      `Dieser Sitzung fehlen ${missing} der ${total} Berechtigungen, um die Visious bittet, deshalb werden die Seiten abgelehnt, die sie brauchen. Eine neue Anmeldung bittet um die ganze Liste und ersetzt sie.`,
    expiringSoon:
      'Diese Sitzung läuft bald ab. Eine neue Anmeldung kostet jetzt zwei Klicks und erspart dir später einen Feed, der aufhört zu laden.',
  },

  signIn: {
    signedOut: 'Auf der Instanz anmelden',
    expired: 'Neu anmelden',
    unreadable: 'Gespeicherte Sitzung ersetzen',
    underScoped: 'Mit allen Berechtigungen neu anmelden',
    active: 'Neu anmelden',
  },

  permission: {
    subscriptions: {
      title: 'Deine Abos',
      detail:
        'Lesen, welchen Kanälen du folgst, und aus Visious heraus abonnieren oder Abos beenden.',
    },
    playlists: {
      title: 'Deine Playlists',
      detail:
        'Deine eigenen und privaten Playlists lesen, sie anlegen und umbenennen, und Videos hinzufügen oder entfernen.',
    },
    feed: {
      title: 'Dein Abo-Feed',
      detail: 'Die Liste neuer Videos aus den Kanälen lesen, denen du folgst.',
    },
    preferences: {
      title: 'Deine Instanz-Einstellungen',
      detail:
        'Die auf der Instanz gespeicherten Einstellungen lesen und die zurückschreiben, die Visious spiegelt.',
    },
    history: {
      title: 'Dein Wiedergabeverlauf',
      detail: 'Lesen, was du gesehen hast, neue Wiedergaben aufzeichnen und Einträge entfernen.',
    },
    tokens: {
      title: 'Automatische Erneuerung',
      detail:
        'Diese Sitzung vor dem Ablauf durch eine frische ersetzen. Sie kann nur Sitzungen erzeugen, die auf dieselben Berechtigungen beschränkt sind, und nur sich selbst beenden, nie eine andere Sitzung.',
    },
    undescribed: 'Für diese Berechtigung gibt es noch keine Beschreibung.',
  },

  noInstance: {
    title: 'Visious hat keine Instanz, bei der es sich anmelden könnte',
    detail:
      'Es ist keine Instanz eingerichtet, es gibt also nichts, wo eine Anmeldung hinführt. Wähle eine unter Einstellungen.',
    retryable: true,
  },

  verify: {
    notFound: {
      title: 'Diese Instanz hat keine Konten',
      detail:
        'Sie bietet die Kontofunktionen nicht an, für die Visious sich anmeldet, es gibt also nichts, wo eine Anmeldung hinführt.',
      retryable: false,
    },
    unreadableTitle: 'Die Instanz hat mit etwas Unerwartetem geantwortet',
    unreadableDetail:
      'Der Aufruf war erlaubt, die Sitzung funktioniert also, und was zurückkam, war nichts, das Visious lesen konnte.',
    disabled: {
      title: 'Diese Instanz hat ihre Konten abgeschaltet',
      detail:
        'Eine Anmeldung kann nicht helfen: die Administration hat die Kontofunktionen abgeschaltet.',
      retryable: false,
    },
  },

  page: {
    grantPanelLabel: 'Anmelden',
    sessionPanelLabel: 'Diese Sitzung',
    sessionHeading: 'Diese Sitzung',
    permissionsPanelLabel: 'Berechtigungen',
    footnoteBefore: 'Visious sieht dein Instanz-Passwort nie. Die Freigabe passiert auf',
    footnoteAfter: 'selbst, und was zurückkommt, ist auf die Berechtigungen oben beschränkt.',
    whereYouWere: 'dort, wo du warst',
    returnAfter: (destination) => `Du kommst danach direkt zu ${destination} zurück.`,
    continueTo: (destination) => `Weiter zu ${destination}`,
    checkingSession: 'Dieser Browser wird auf eine vorhandene Sitzung geprüft…',
    factAccount: 'Konto',
    factInstance: 'Instanz',
    factExpires: 'Läuft ab',
    factGranted: 'Erteilt',
    factMissing: 'Nicht bekannt',
    signOut: 'Abmelden',
    checkAgain: 'Erneut prüfen',
    signOutNote:
      'Beim Abmelden wird die Sitzung aus diesem Browser gelöscht. Mit erteilter automatischer Erneuerung bittet Visious die Instanz außerdem, sie zu beenden, und diese Berechtigung reicht für das Beenden der eigenen Sitzung und keiner anderen. Ohne sie nimm die Token-Liste der Instanz selbst.',
    verifyChecking: 'Die Instanz wird gefragt, ob diese Sitzung noch funktioniert…',
    verifyOkEmpty: 'Die Instanz hat sie angenommen. Du folgst keinem Kanal.',
    verifyOk: (count) =>
      `Die Instanz hat sie angenommen. Du folgst ${count} ${count === 1 ? 'Kanal' : 'Kanälen'}.`,
    verifyRefusedSignedOut:
      'Die Instanz hat diese Sitzung abgelehnt, sie ist also nicht nutzbar. Eine neue Anmeldung ersetzt sie.',
    verifyRefused: 'Die Instanz hat das abgelehnt. Der Hinweis oben sagt, welchen Grund sie genannt hat.',
    permissionsHeadingGranted: 'Was diese Sitzung darf',
    permissionsHeadingWanted: 'Worum Visious bitten wird',
    granted: 'erteilt',
    notGranted: 'nicht erteilt',
    /* Das `<code>:*</code>` steht *zwischen* den beiden Hälften, das Substantiv gehört also wie im
       Englischen dahinter — sonst hinge der Punkt frei hinter dem Codeschnipsel. */
    scopeNoteBefore: 'Das ist die ganze Liste. Visious bittet nicht um die instanzweite',
    scopeNoteAfter:
      'Berechtigung. Die automatische Erneuerung ist die eine Ergänzung, die du wählen kannst, und sie ist eng gefasst: sie erzeugt Sitzungen, die auf dieselben Berechtigungen beschränkt sind, und kann nur sich selbst beenden.',
    renewLabel: 'Diese Sitzung automatisch erneuern',
    renewDetail:
      'Bittet um eine Berechtigung mehr, damit die Sitzung sich selbst ersetzen kann, bevor ihre 30 Tage ablaufen. Ohne sie bittet Visious dich stattdessen um eine neue Anmeldung.',
  },
};
